import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  FiX,
  FiUser,
  FiCalendar,
  FiTag,
  FiActivity,
  FiFileText,
  FiPlus,
  FiMinus,
  FiCode
} from 'react-icons/fi';
import labTemplateService from '../../services/labTemplateService';
import LaboratoryOrdersService from '../../services/laboratoryOrdersService';
import { API_DOCTOR_LIST } from '../../../../helpers/urlHelper';

const AddNewLaboratoryOrder = ({ 
  patientId, 
  onSubmit, 
  onCancel, 
  isSubmitting = false 
}) => {
  // State for templates and doctors
  const [labTemplates, setLabTemplates] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [selectedParameters, setSelectedParameters] = useState({});
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  
  // Enhanced form state for new order
  const [newOrder, setNewOrder] = useState({
    patient_id: patientId,
    templates: [],
    parameters: {},
    priority: 'routine',
    status: 'pending',
    order_date: new Date().toISOString().split('T')[0],
    expected_results_date: '',
    notes: '',
    ordered_by_id: '',
    ordered_by_name: '',
    department: 'laboratory'
  });

  // Load lab templates - memoized to avoid dependency issues
  const loadLabTemplates = useCallback(async () => {
    setIsLoadingTemplates(true);
    try {
      const response = await labTemplateService.getAllTemplates();
      console.log('Lab templates response:', response);
      
      let templatesData = [];
      if (response.data) {
        templatesData = Array.isArray(response.data) ? response.data : [response.data];
      } else if (Array.isArray(response)) {
        templatesData = response;
      }
      
      setLabTemplates(templatesData);
    } catch (error) {
      console.error('Error loading lab templates:', error);
      setLabTemplates([]);
    } finally {
      setIsLoadingTemplates(false);
    }
  }, []);

  // Fetch doctors from API - memoized
  const fetchDoctors = useCallback(async () => {
    try {
      const doctorsResponse = await fetch(`${API_DOCTOR_LIST}`);
      if (!doctorsResponse.ok) {
        throw new Error('Failed to fetch doctors');
      }
      const doctorsData = await doctorsResponse.json();
      
      const doctorsArray = doctorsData && doctorsData.data ? doctorsData.data : [];
      setDoctors(doctorsArray);
    } catch (err) {
      console.error('Error fetching doctors:', err);
      setDoctors([]);
    }
  }, []);

  // Load initial data
  useEffect(() => {
    if (patientId) {
      loadLabTemplates();
      fetchDoctors();
      setNewOrder(prev => ({
        ...prev,
        patient_id: patientId
      }));
    }
  }, [patientId, loadLabTemplates, fetchDoctors]);

  const handleFormChange = (field, value) => {
    setNewOrder(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Generate consistent parameter ID
  const generateParameterId = (templateId, parameterIndex, parameter) => {
    // Use parameter._id if exists, otherwise generate consistent ID
    return parameter._id || `${templateId}-param-${parameterIndex}`;
  };

  // Handle template addition
  const handleAddTemplate = (templateId) => {
    const template = labTemplates.find(t => t._id === templateId);
    if (!template) {
      console.error('Template not found:', templateId);
      return;
    }

    // Check if template is already selected
    const isAlreadySelected = selectedTemplates.some(t => t._id === templateId);
    if (isAlreadySelected) {
      console.log('Template already selected:', templateId);
      return;
    }

    console.log('Adding template:', template);

    setSelectedTemplates(prev => [...prev, template]);
    
    // Initialize parameters for this template (no parameters selected by default)
    setSelectedParameters(prev => ({
      ...prev,
      [templateId]: []
    }));
  };

  // Handle template removal
  const handleRemoveTemplate = (templateId) => {
    setSelectedTemplates(prev => prev.filter(t => t._id !== templateId));
    
    // Remove parameters for this template
    setSelectedParameters(prev => {
      const newParams = { ...prev };
      delete newParams[templateId];
      return newParams;
    });
  };

  // Handle parameter selection within a template - FIXED
  const handleParameterSelection = (templateId, parameterId) => {
    console.log('Selecting parameter:', { templateId, parameterId });
    
    setSelectedParameters(prev => {
      const currentParams = prev[templateId] || [];
      const isSelected = currentParams.includes(parameterId);
      
      const newParams = isSelected 
        ? currentParams.filter(id => id !== parameterId)
        : [...currentParams, parameterId];
      
      console.log('Updated parameters for template:', {
        templateId,
        before: currentParams,
        after: newParams
      });
      
      return {
        ...prev,
        [templateId]: newParams
      };
    });
  };

  // Handle doctor selection
  const handleDoctorSelection = (doctorId) => {
    const selectedDoctor = doctors.find(d => d._id === doctorId);
    if (selectedDoctor) {
      setNewOrder(prev => ({
        ...prev,
        ordered_by_id: selectedDoctor._id,
        ordered_by_name: selectedDoctor.name          
      }));
    }
  };

  // Select all parameters for a template - FIXED
  const handleSelectAllParameters = (templateId) => {
    const template = selectedTemplates.find(t => t._id === templateId);
    if (template && template.parameters && Array.isArray(template.parameters)) {
      const allParameterIds = template.parameters.map((param, index) => {
        return generateParameterId(templateId, index, param);
      });
      
      setSelectedParameters(prev => ({
        ...prev,
        [templateId]: allParameterIds
      }));
    }
  };

  // Deselect all parameters for a template
  const handleDeselectAllParameters = (templateId) => {
    setSelectedParameters(prev => ({
      ...prev,
      [templateId]: []
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsCreatingOrder(true);
    
    try {
      // Transform data to match backend model
      const selectedTests = selectedTemplates.map(template => {
        const templateId = template._id;
        const selectedParamsForTemplate = selectedParameters[templateId] || [];
        
        return {
          testTemplateId: templateId,
          testName: template.name,
          testCode: template.code,
          category: template.category,
          priority: newOrder.priority === 'routine' ? 'normal' : 'urgent',
          selectedParameters: selectedParamsForTemplate // Mantener la lista de IDs
        };
      });

      const orderData = {
        patientId: patientId,
        selectedTests: selectedTests,
        
        // AGREGAR PARÁMETROS SELECCIONADOS AL PAYLOAD
        selectedParameters: selectedParameters, // Esta es la clave que faltaba
        
        type: 'Petición analítica',
        requestingTeam: newOrder.ordered_by_name || '',
        reason: newOrder.notes || '',
        clinicalData: newOrder.notes || '',
        isUrgent: ['urgent', 'critical'].includes(newOrder.priority.toLowerCase()),
        status: newOrder.status || 'pending',
        
        orderDate: newOrder.order_date || new Date().toISOString().split('T')[0],
        expectedResultsDate: newOrder.expected_results_date || null,
        priority: newOrder.priority,
        orderedById: newOrder.ordered_by_id,
        orderedByName: newOrder.ordered_by_name,
        notes: newOrder.notes,
        
        department: newOrder.department
      };

      console.log('Creating laboratory order with parameters:', {
        ...orderData,
        selectedParametersDetails: Object.entries(selectedParameters).map(([templateId, params]) => ({
          templateId,
          parameterCount: params.length,
          parameters: params
        }))
      });
      
      const response = await LaboratoryOrdersService.createOrder(orderData);
      
      console.log('Order created successfully:', response);
      
      if (onSubmit) {
        await onSubmit(response);
      }
      
      setTimeout(() => {
        window.location.reload();
      }, 100);
      
    } catch (error) {
      console.error('Error creating laboratory order:', error);
      
      if (error.response) {
        console.error('Server error status:', error.response.status);
        console.error('Server error data:', error.response.data);
        console.error('Server error headers:', error.response.headers);
        
        if (error.response.status === 400) {
          console.error('Validation errors:', error.response.data.errors);
        } else if (error.response.status === 404) {
          console.error('Endpoint not found - check your API URL');
        } else if (error.response.status === 500) {
          console.error('Internal server error');
        }
      } else if (error.request) {
        console.error('Network error - no response received:', error.request);
      } else {
        console.error('Request setup error:', error.message);
      }
      
      if (onCancel) {
        onCancel();
      }
      
      setTimeout(() => {
        window.location.reload();
      }, 100);
      
    } finally {
      setIsCreatingOrder(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'urgent':
      case 'critical':
        return 'danger';
      case 'high':
        return 'warning';
      case 'routine':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  // Group available templates by category (excluding already selected)
  const availableTemplatesByCategory = useMemo(() => {
    const selectedIds = selectedTemplates.map(t => t._id);
    const availableTemplates = labTemplates.filter(t => !selectedIds.includes(t._id));
    
    const grouped = {};
    availableTemplates.forEach(template => {
      const category = template.category || 'Other';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(template);
    });
    return grouped;
  }, [labTemplates, selectedTemplates]);

  // Check if form is valid for submission
  const isFormValid = useMemo(() => {
    return (
      selectedTemplates.length > 0 &&
      newOrder.ordered_by_id &&
      newOrder.order_date &&
      // At least one parameter must be selected for each template
      selectedTemplates.every(template => {
        const templateParams = selectedParameters[template._id] || [];
        return templateParams.length > 0;
      })
    );
  }, [selectedTemplates, selectedParameters, newOrder.ordered_by_id, newOrder.order_date]);

  return (
    <div className="h-100 d-flex flex-column">
      {/* Order Information */}
      <div className="mb-4">
        <h6 className="text-muted mb-3 d-flex align-items-center gap-2">
          <FiCalendar />
          Order Information
        </h6>
        
        {/* Order Date */}
        <div className="mb-3">
          <label className="form-label">Order Date *</label>
          <input
            type="date"
            className="form-control"
            value={newOrder.order_date}
            onChange={(e) => handleFormChange('order_date', e.target.value)}
            required
          />
        </div>

        {/* Expected Results Date */}
        <div className="mb-3">
          <label className="form-label">Expected Results Date</label>
          <input
            type="date"
            className="form-control"
            value={newOrder.expected_results_date}
            onChange={(e) => handleFormChange('expected_results_date', e.target.value)}
          />
        </div>

        {/* Priority */}
        <div className="mb-3">
          <label className="form-label d-flex align-items-center gap-2">
            <FiTag />
            Priority *
          </label>
          <select
            className="form-select"
            value={newOrder.priority}
            onChange={(e) => handleFormChange('priority', e.target.value)}
            required
          >
            <option value="routine">Routine</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
            <option value="critical">Critical</option>
          </select>
        </div>

        {/* Ordered By */}
        <div className="mb-3">
          <label className="form-label d-flex align-items-center gap-2">
            <FiUser />
            Ordered By *
          </label>
          <select
            className="form-select"
            value={newOrder.ordered_by_id}
            onChange={(e) => handleDoctorSelection(e.target.value)}
            required
          >
            <option value="">Select Doctor</option>
            {doctors.map((doctor) => (
              <option key={doctor._id} value={doctor._id}>
                {doctor.name} {doctor.surname} ({doctor.role})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Add Templates Section */}
      <div className="mb-4">
        <h6 className="text-muted mb-3 d-flex align-items-center gap-2">
          <FiActivity />
          Add Laboratory Templates *
        </h6>
        
        {isLoadingTemplates ? (
          <div className="text-center py-3">
            <div className="spinner-border spinner-border-sm" role="status"></div>
            <span className="ms-2">Loading templates...</span>
          </div>
        ) : (
          <div className="mb-3">
            <label className="form-label">Select Template to Add</label>
            <select
              className="form-select"
              onChange={(e) => {
                if (e.target.value) {
                  handleAddTemplate(e.target.value);
                  e.target.value = '';
                }
              }}
              value=""
            >
              <option value="">Choose a template to add...</option>
              {Object.entries(availableTemplatesByCategory).map(([category, templates]) => (
                <optgroup key={category} label={category}>
                  {templates.map((template) => (
                    <option key={template._id} value={template._id}>
                      {template.name} ({template.loincCode})
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Selected Templates - COMPACT VERSION */}
      <div className="flex-grow-1 mb-4">
        {selectedTemplates.length > 0 && (
          <div className="mb-4">
            <h6 className="text-muted mb-3">Selected Templates ({selectedTemplates.length})</h6>
            
            {selectedTemplates.map((template) => {
              const templateId = template._id;
              const templateParams = selectedParameters[templateId] || [];
              const hasParameters = template.parameters && Array.isArray(template.parameters) && template.parameters.length > 0;
              const allParametersSelected = hasParameters && templateParams.length === template.parameters.length;
              const noParametersSelected = templateParams.length === 0;
              
              return (
                <div key={templateId} className="card mb-3 border-primary">
                  <div className="card-header bg-light py-2">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="flex-grow-1 me-3">
                        <h6 className="mb-1 text-primary">{template.name}</h6>
                        <div className="d-flex flex-wrap gap-2 align-items-center">
                          <span className="badge bg-secondary">{template.code}</span>
                          {template.loincCode && (
                            <span className="badge bg-info d-flex align-items-center gap-1">
                              <FiCode size={12} />
                              LOINC: {template.loincCode}
                            </span>
                          )}
                          <span className="badge bg-success">{template.category}</span>
                        </div>
                        {template.description && (
                          <small className="text-muted d-block mt-1">
                            {template.description}
                          </small>
                        )}
                      </div>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => handleRemoveTemplate(templateId)}
                        title="Remove template"
                      >
                        <FiX />
                      </button>
                    </div>
                  </div>
                  
                  {/* Template Parameters - COMPACT VERSION */}
                  <div className="card-body py-2">
                    {hasParameters ? (
                      <>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <small className="text-primary fw-bold">
                            <FiActivity className="me-1" size={14} />
                            Parameters ({templateParams.length}/{template.parameters.length})
                          </small>
                          <div>
                            <button
                              type="button"
                              className="btn btn-outline-success btn-sm me-1"
                              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                              onClick={() => handleSelectAllParameters(templateId)}
                              disabled={allParametersSelected}
                            >
                              <FiPlus size={12} className="me-1" />
                              All
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-warning btn-sm"
                              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                              onClick={() => handleDeselectAllParameters(templateId)}
                              disabled={noParametersSelected}
                            >
                              <FiMinus size={12} className="me-1" />
                              Clear
                            </button>
                          </div>
                        </div>
                        
                        {/* Alert if no parameters selected */}
                        {noParametersSelected && (
                          <div className="alert alert-danger py-1 mb-2" style={{ fontSize: '0.8rem' }}>
                            <strong>⚠️</strong> Select at least one parameter to proceed.
                          </div>
                        )}
                        
                        <div className="row g-2">
                          {template.parameters.map((parameter, index) => {
                            // Generate consistent parameter ID
                            const parameterId = generateParameterId(templateId, index, parameter);
                            const isParameterSelected = templateParams.includes(parameterId);
                            
                            return (
                              <div key={parameterId} className="col-md-6">
                                <div className={`form-check p-2 border rounded ${isParameterSelected ? 'bg-primary bg-opacity-10 border-primary' : 'bg-light'}`} style={{ fontSize: '0.85rem' }}>
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={isParameterSelected}
                                    onChange={() => handleParameterSelection(templateId, parameterId)}
                                    id={`param-checkbox-${templateId}-${index}`}
                                  />
                                  <label 
                                    className="form-check-label w-100" 
                                    htmlFor={`param-checkbox-${templateId}-${index}`}
                                  >
                                    <div className="fw-bold text-dark mb-1">
                                      {parameter.name || `Parameter ${index + 1}`}
                                    </div>
                                    <div className="d-flex flex-wrap gap-1 mb-1">
                                      {parameter.loincCode && (
                                        <span className="badge bg-primary" style={{ fontSize: '0.7rem' }}>
                                          <FiCode size={8} />
                                          {parameter.loincCode}
                                        </span>
                                      )}
                                      {parameter.unit && (
                                        <span className="badge bg-secondary" style={{ fontSize: '0.7rem' }}>
                                          {parameter.unit}
                                        </span>
                                      )}
                                    </div>
                                    {parameter.referenceRange && (
                                      <div className="small text-muted">
                                        <strong>Range:</strong> {' '}
                                        {parameter.referenceRange.normal ? (
                                          <span className="text-success">{parameter.referenceRange.normal}</span>
                                        ) : (
                                          <span>
                                            {parameter.referenceRange.min || '–'} - {parameter.referenceRange.max || '–'}
                                            {parameter.unit ? ` ${parameter.unit}` : ''}
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </label>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    ) : (
                      <div className="alert alert-warning py-2" style={{ fontSize: '0.85rem' }}>
                        <strong>No Parameters:</strong> This template has no parameters to configure.
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Notes */}
      <div className="mb-4">
        <label className="form-label d-flex align-items-center gap-2">
          <FiFileText />
          Notes
        </label>
        <textarea
          className="form-control"
          rows="3"
          value={newOrder.notes}
          onChange={(e) => handleFormChange('notes', e.target.value)}
          placeholder="Additional notes or instructions..."
        />
      </div>

      {/* Order Summary */}
      {selectedTemplates.length > 0 && (
        <div className="mb-4">
          <h6 className="text-muted mb-2">Order Summary</h6>
          <div className="bg-light p-3 rounded">
            <div className="mb-2">
              <strong>Selected Templates:</strong>
            </div>
            {selectedTemplates.map((template) => {
              const templateId = template._id;
              const selectedParamsCount = (selectedParameters[templateId] || []).length;
              const totalParamsCount = template.parameters ? template.parameters.length : 0;
              const hasSelectedParams = selectedParamsCount > 0;
              
              return (
                <div key={templateId} className="small mb-1">
                  • <strong>{template.name}</strong> ({template.category})
                  {totalParamsCount > 0 && (
                    <span className={`ms-2 ${!hasSelectedParams ? 'text-danger' : 'text-muted'}`}>
                      - {selectedParamsCount}/{totalParamsCount} parameters
                      {!hasSelectedParams && <span className="fw-bold"> (Required!)</span>}
                    </span>
                  )}
                </div>
              );
            })}
            <hr className="my-2" />
            <div className="d-flex justify-content-between small">
              <span>Total Templates:</span>
              <span>{selectedTemplates.length}</span>
            </div>
            <div className="d-flex justify-content-between small">
              <span>Total Parameters:</span>
              <span>
                {Object.values(selectedParameters).reduce((sum, params) => sum + params.length, 0)}
              </span>
            </div>
            <div className="d-flex justify-content-between small">
              <span>Priority:</span>
              <span className={`badge bg-${getPriorityColor(newOrder.priority)}`}>
                {newOrder.priority}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Footer with Submit Buttons */}
      <div className="mt-auto pt-3 border-top">
        <div className="d-flex justify-content-between">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onCancel}
            disabled={isCreatingOrder}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={isCreatingOrder || !isFormValid}
          >
            {isCreatingOrder ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                Creating Order...
              </>
            ) : (
              'Create Order'
            )}
          </button>
        </div>
        
        {/* Validation messages */}
        {selectedTemplates.length === 0 && (
          <small className="text-danger d-block mt-2">
            Please select at least one laboratory template.
          </small>
        )}
        {selectedTemplates.length > 0 && !isFormValid && (
          <small className="text-danger d-block mt-2">
            Please select at least one parameter for each template and ensure all required fields are filled.
          </small>
        )}
      </div>
    </div>
  );
};

export default AddNewLaboratoryOrder;